import { useQueries } from '@tanstack/react-query';
import type { FieldTypeMetadata } from 'src/data-capture/types';
import type { DynamicUiData, EntitiesByType, EntityWithMetadata } from 'src/UI/dynamicUI/types';

export type ProfessionalConnection = {
  id: string;
  clientId: string;
  name: string;
  // The connection's role as a person would write it ("Tax adviser"), not the ontology's own
  // name for it ("TaxAdvisor"). Callers show this and save it as a participant's description,
  // so the raw enum would reach the meeting note and the attendee list.
  type: string;
  email: string;
};

/**
 * Professional connections are `ProfessionalConnection` entities stored on each
 * client's non-financial data, gathered here across the given clients.
 */
export function useProfessionalConnections(clientIds: string[]): {
  connections: ProfessionalConnection[];
  isLoading: boolean;
} {
  const results = useQueries({
    queries: clientIds.map(clientId => ({
      queryKey: ['clients', clientId, 'non-financial-data'],
    })),
  });

  const connections = results.flatMap((result, index) =>
    toConnections(clientIds[index], (result.data as DynamicUiData | undefined)?.entities),
  );
  return {
    connections: [...new Map(connections.map(connection => [connection.id, connection])).values()],
    isLoading: results.some(result => result.isLoading),
  };
}

/**
 * Key contacts are stored on a meeting as plain external participants, so they are
 * recognised by name. Compare names through this key so case and spacing differences
 * don't break the match.
 */
export function connectionNameKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The label the ontology gives a SELECT value, falling back to the value itself. The backend
 * ships the options with the entity, so nothing here has to know the enum's members.
 */
function selectLabel(value: string, metadata: FieldTypeMetadata | undefined): string {
  if (!value || metadata?.type !== 'SELECT') return value;
  return metadata.options.find(option => String(option.value) === value)?.label ?? value;
}

export function toConnections(
  clientId: string,
  entities: EntitiesByType | undefined,
): ProfessionalConnection[] {
  const value = entities?.ProfessionalConnection;
  if (!value) return [];
  const list: EntityWithMetadata[] = Array.isArray(value) ? value : [value];
  return list
    .map(entity => {
      const fields = entity.entity?.fields ?? {};
      const rawType = String(fields.type ?? '').trim();
      return {
        id: entity.id,
        clientId,
        name: String(fields.name ?? '').trim(),
        type: selectLabel(rawType, entity.entity?.metadata?.type),
        email: String(fields.email ?? '').trim(),
      };
    })
    .filter(connection => connection.name.length > 0);
}
